import React, { useState, useCallback } from 'react';
import { Modal, Fade, Paper, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { HelpModalContext } from '../contexts/HelpModalContext';
import * as documentationService from '../services/documentationService'; // Adjust path if necessary

// This is a mapping from a documentationKey to the actual filename/path for the documentationService
// You'll need to expand this as you add help to more sections.
const documentationRegistry = {
  'FormasDePagamento': 'Cadastros/Operacionais/Formas-de-Pagamento.md',
  'CategoriasDeCliente': 'Cadastros/Operacionais/Categorias-de-Cliente.md',
  'ModalidadesDeFrete': 'Cadastros/Operacionais/Modalidades-de-Frete.md',
  'DominiosEmailsComerciais': 'Cadastros/Operacionais/Domínios-de-Emails-Comerciais.md',
  'PlanosDePagamento': 'Cadastros/Operacionais/Planos-de-Pagamento.md',
  'AlcadasDeAprovacao': 'Cadastros/Operacionais/Alçadas-de-Aprovação.md',
  'BandeirasDeCartao': 'Cadastros/Operacionais/Bandeiras-de-Cartão.md',
  'Produtos': 'Cadastros/Operacionais/Produtos.md',
  'FiltrosDeProdutos': 'Cadastros/Operacionais/Filtros-de-Produtos.md',
  'AreasDeNegocio': 'Cadastros/Operacionais/Áreas-de-Negócio.md',
  'LojasPontosDeVenda': 'Cadastros/Operacionais/Lojas-e-Pontos-de-Venda.md',
  'TiposDePromocao': 'Cadastros/Operacionais/Tipos-de-Promoção.md',
  'EstruturaDeCadastro': 'Cadastros/Operacionais/Estruturas-de-Cadastro.md',
  'TiposDeAtributo': 'Cadastros/Operacionais/Tipos-de-Atributo.md',
  'ValorDoAtributo': 'Cadastros/Operacionais/Valor-do-Atributo.md',
  'GruposEconomicos': 'Cadastros/Operacionais/Grupos-Econômicos.md',
  'CanaisDeVenda': 'Cadastros/Operacionais/Canais-de-Venda.md',
  'Sellers': 'Cadastros/Operacionais/Sellers.md',
  'Parametros': 'Cadastros/Seguraça/Parametrizações.md'
  // Example for another section:
  // 'Usuarios': 'Administracao/Usuarios/GerenciamentoDeUsuarios.md',
};

export const HelpModalProvider = ({ children }) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [helpModalContent, setHelpModalContent] = useState('');
  const [modalTitle, setModalTitle] = useState('Ajuda'); // Optional: make title dynamic too

  const openHelpModal = useCallback(async (documentationKey, title = 'Ajuda') => {
    console.log('[HelpModalProvider] openHelpModal called with key:', documentationKey, 'and title:', title);
    const filePath = documentationRegistry[documentationKey];
    if (!filePath) {
      console.error(`[HelpModalProvider] No documentation found for key: ${documentationKey}`);
      setHelpModalContent('Conteúdo da ajuda não encontrado para esta seção.');
      setModalTitle(title); // Set title even if content not found
      setIsHelpModalOpen(true);
      console.log('[HelpModalProvider] State after no filePath: isHelpModalOpen:', true);
      return;
    }

    try {
      const encodedFile = encodeURIComponent(filePath);
      console.log('[HelpModalProvider] Fetching documentation for encoded file:', encodedFile);
      const response = await documentationService.get(encodedFile);
      if (response && response.data) {
        console.log('[HelpModalProvider] Successfully fetched content.');
        setHelpModalContent(response.data);
        setModalTitle(title);
      } else {
        console.error(`[HelpModalProvider] Failed to fetch help content for: ${documentationKey}, response:`, response);
        setHelpModalContent('Erro ao carregar o conteúdo da ajuda.');
        setModalTitle(title);
      }
    } catch (error) {
      console.error(`[HelpModalProvider] Error fetching help content for ${documentationKey}:`, error);
      setHelpModalContent('Erro ao carregar o conteúdo da ajuda.');
      setModalTitle(title);
    }
    setIsHelpModalOpen(true);
    console.log('[HelpModalProvider] State after trying to fetch/set content: isHelpModalOpen:', true);
  }, []);

  const closeHelpModal = useCallback(() => {
    console.log('[HelpModalProvider] closeHelpModal called.');
    setIsHelpModalOpen(false);
    setHelpModalContent(''); // Clear content on close
    console.log('[HelpModalProvider] State after closeHelpModal: isHelpModalOpen:', false);
  }, []);

  console.log('[HelpModalProvider] Rendering. isHelpModalOpen:', isHelpModalOpen, 'Content length:', helpModalContent.length);

  return (
    <HelpModalContext.Provider value={{ isHelpModalOpen, helpModalContent, openHelpModal, closeHelpModal }}>
      {children}
      <Modal
        open={isHelpModalOpen}
        onClose={closeHelpModal}
        aria-labelledby="global-help-modal-title"
        aria-describedby="global-help-modal-description"
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isHelpModalOpen}>
          <Paper sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: (theme) => theme.shadows[5],
            p: 3,
          }}>
            <Typography 
              id="global-help-modal-title" 
              variant="h5" // Consistent with user's last change
              sx={{ 
                color: 'primary.main',
                mb: 2,
                fontWeight: 'bold'
              }}
            >
              {modalTitle}
            </Typography>
            {/* 
              To further customize the appearance of Markdown elements (e.g., h1, p, code, etc.), 
              you can use the 'components' prop of ReactMarkdown. 
              Example: <ReactMarkdown components={{ h1: ({node, ...props}) => <Typography variant="h4" {...props} /> }} />
            */}
            <Typography component="div" id="global-help-modal-description" sx={{ mt: 2 }}>
              <ReactMarkdown>{helpModalContent}</ReactMarkdown>
            </Typography>
          </Paper>
        </Fade>
      </Modal>
    </HelpModalContext.Provider>
  );
}; 